import { Button, Grid2, Typography, Skeleton } from '@mui/material';
import { Box } from '@mui/material';
import { useEffect, Fragment } from 'react';
import { useState } from 'react';
import VaultsTable from '../components/dashboard/VaultsTable';
import rankIcon from '../assets/rank.svg';
import { useAccount } from 'wagmi';
import infoIcon from '../assets/info.svg';
import { DashboardConnectButton } from '../components/dashboard/DashboardConnectButton';
import relendUnitsIcon from '../assets/dashboard/relendUnits.svg';
import MobileVaultsCards from '../components/dashboard/MobileVaultsCards';
import LeaderboardModal from '../components/dashboard/LeaderboardModal';
import { RewardData, useAppContext } from '../context/context';
import { FriendlyFormatNumber } from '../Utils';
import styles from '../styles/styles';

const isMobile = window.innerWidth < 600;
const DASHBOARD_CONSTANTS = {
  MAX_LEADERBOARD_DISPLAY: isMobile ? 3 : 10,
  TRUNCATE_ADDRESS: {
    START: 6,
    END: 4
  }
};

const formatRelendUnits = (relendUnits: string): string => {
  const num = parseFloat(relendUnits);
  if (isNaN(num)) return '0';
  return num >= 1000 ? `${(num / 1000).toFixed(1)}k units` : `${num.toFixed(0)} units`;
};

const truncateAddress = (address: string): string => {
  const { START, END } = DASHBOARD_CONSTANTS.TRUNCATE_ADDRESS;
  return `${address.slice(0, START)}...${address.slice(-END)}`;
};

const LeaderboardRow = ({ reward, index }: { reward: RewardData; index: number }) => {
  return (
    <Box sx={styles.leaderboardRow}>
      {Object.entries(reward).map(([key, value]) => (
        <Fragment key={key}>
          <Typography sx={{ color: '#fff', fontSize: '1rem' }}>{`${index + 1}. ${truncateAddress(key)}`}</Typography>
          <Typography sx={{ color: '#fff', fontSize: '1rem' }}>{formatRelendUnits(value)}</Typography>
        </Fragment>
      ))}
    </Box>
  );
};

export default function Dashboard() {
  const [isLoading, setIsLoading] = useState(true);
  const { address, isConnected } = useAccount();
  const [userRank, setUserRank] = useState<number>(0);
  const [userRelendUnits, setUserRelendUnits] = useState<number>(0);
  const [isLeaderboardModalOpen, setIsLeaderboardModalOpen] = useState(false);
  const { vaults, rewardData, userBalances } = useAppContext();

  useEffect(() => {
    const fetchRewardData = async () => {
      setIsLoading(true);
      try {
        if (address && rewardData[address]) {
          setUserRelendUnits(Number(rewardData[address]));
          const index = Object.entries(rewardData).findIndex(([addr]) => addr === address);
          setUserRank(index);
        } else {
          setUserRelendUnits(-1);
          setUserRank(-1);
        }
      } catch (error) {
        console.error('Failed to fetch reward data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRewardData();
    if (!isConnected) {
      setUserRelendUnits(0);
      setUserRank(0);
      setIsLoading(false);
    }
  }, [address, isConnected]);

  const boxColumns = {
    xs: 6,
    md: 3,
    lg: 2
  };

  return (
    <Box width="100%" height="100%" px={{ xs: 2 }}>
      <Grid2
        container
        width="100%"
        height="100%"
        rowSpacing={2}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '2rem'
        }}
      >
        <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 8 }} sx={{ my: 4 }}>
          <Typography sx={styles.gradientText}>Earn RELEND units, MORPHO tokens and APY</Typography>
        </Grid2>

        {!address && (
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 8 }}>
            <Box sx={styles.connectWalletBox}>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                <img src={infoIcon} alt="info" />
                <Typography>Connect wallet to view</Typography>
              </Box>
              <DashboardConnectButton />
            </Box>
          </Grid2>
        )}

        <Grid2
          container
          spacing={2}
          width="100%"
          height="100%"
          sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}
        >
          <Grid2 className="deposits" size={boxColumns}>
            <Box sx={styles.boxStyle} minHeight={{ xs: 170, md: '100%' }}>
              <Typography
                fontSize={styles.boxFontSize}
                sx={{ color: !address ? 'rgba(255, 255, 255, 0.4)' : 'inherit' }}
                variant="h6"
              >
                Deposits
              </Typography>
              <Typography fontSize={styles.boxFontSize}>
                {isLoading ? (
                  <Skeleton width={100} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                ) : isConnected && userBalances.global ? (
                  `$${userBalances.global.toFixed(2)}`
                ) : (
                  '$0'
                )}
              </Typography>
            </Box>
          </Grid2>

          <Grid2 className="relendUnits" size={boxColumns}>
            <Box
              minHeight={{ xs: 170, md: '100%' }}
              sx={{
                ...styles.boxStyle,
                position: 'relative',
                background: isConnected
                  ? 'radial-gradient(75% 75.89% at 47.12% 20%, rgba(40, 107, 217, 0.4) 0%, rgba(40, 107, 217, 0) 100%), rgba(1, 171, 146, 0.12)'
                  : 'rgba(1, 171, 146, 0.12)'
              }}
            >
              {isConnected && (
                <img
                  src={relendUnitsIcon}
                  alt="relendUnits"
                  style={{
                    position: 'absolute',
                    top: 7,
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    borderImageSource: 'radial-gradient(11.67% 11.67% at 50% 88.33%, #00304D 0%, #00304D 100%)',
                    borderRadius: '1.5625rem',
                    width: '25%'
                  }}
                />
              )}
              <Typography
                fontSize={styles.boxFontSize}
                sx={{ color: !address ? 'rgba(255, 255, 255, 0.4)' : 'inherit' }}
                variant="h6"
              >
                Relend Units
              </Typography>
              <Typography fontSize={styles.boxFontSize} sx={{ textAlign: 'center' }}>
                {isLoading ? (
                  <Skeleton width={60} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                ) : userRelendUnits === -1 ? (
                  '0'
                ) : (
                  Math.floor(userRelendUnits)
                )}
              </Typography>
            </Box>
          </Grid2>

          <Grid2
            className="rank"
            sx={{ color: !address ? 'rgba(255, 255, 255, 0.4)' : 'inherit' }}
            size={boxColumns}
            height="100%"
          >
            <Box sx={styles.boxStyle} minHeight={{ xs: 170, md: '100%' }}>
              <Typography fontSize={styles.boxFontSize} variant="h6">
                Rank
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography fontSize={styles.boxFontSize}>
                  {isLoading ? (
                    <Skeleton width={80} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                  ) : isConnected ? (
                    userRank === -1 ? (
                      'unranked'
                    ) : (
                      userRank + 1
                    )
                  ) : (
                    '0'
                  )}
                </Typography>
                <img src={rankIcon} alt="rank" />
              </Box>
            </Box>
          </Grid2>

          <Grid2 className="tvl" size={boxColumns}>
            <Box sx={styles.boxStyle} minHeight={{ xs: 170, md: '100%' }}>
              <Typography fontSize={styles.boxFontSize} variant="h6">
                TVL
              </Typography>
              <Typography fontSize={styles.boxFontSize} variant="h5">
                {isLoading ? (
                  <Skeleton width={120} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                ) : (
                  `$${FriendlyFormatNumber(vaults.find((vault) => vault.name === 'Relend USDC')?.tvl || 0)}`
                )}
              </Typography>
            </Box>
          </Grid2>
        </Grid2>

        <Grid2
          container
          spacing={4}
          width="100%"
          sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', justifyContent: 'center' }}
        >
          <Grid2 className="vaults" size={{ md: 7, lg: 5 }} display={{ xs: 'none', sm: 'flex' }}>
            <Box sx={{ ...styles.boxStyle, alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box sx={{ width: '100%' }}>
                <Typography sx={styles.leaderboardTitle}>Vault</Typography>
                {vaults && <VaultsTable vaults={vaults} />}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', width: '100%', gap: 1 }}>
                <Box >
                  <img src='/relenddashboardlogo.svg' alt="relend logo" onClick={() => window.open('https://app.morpho.org/ethereum/vault/0x0F359FD18BDa75e9c49bC027E7da59a4b01BF32a/relend-usdc', '_blank')} />
                </Box>
                </Box>
              <Box sx={{ width: '100%' }}>
                <Typography sx={{ fontSize: '1rem', color: 'white', textAlign: 'center' }}>
                  The Relend USDC vault serves as a Global Price Stability Module, maintaining the peg of all rUSDC
                  instances. Users supply USDC, receiving RELEND units and additional relending yield from rUSDC supplied
                  to partner chains. Collateral markets are selected by risk experts at Relend, B.Protocol and Risk DAO to
                  offer secure and competitive yield.
                </Typography>
              </Box>
            </Box>
          </Grid2>
          <Grid2 className="vaults" size={{ md: 5 }} display={{ xs: 'flex', sm: 'none' }} sx={{ width: '100%', height: '100%', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={styles.leaderboardTitle}>Vault</Typography>
              {vaults && <MobileVaultsCards vaults={vaults} />}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', width: '100%', gap: 1 }}>
              <img src='/relenddashboardlogo.svg' alt="relend logo" onClick={() => window.open('https://app.morpho.org/ethereum/vault/0x0F359FD18BDa75e9c49bC027E7da59a4b01BF32a/relend-usdc', '_blank')} />
            </Box>
            <Typography sx={{ fontSize: '1rem', color: 'white', textAlign: 'center' }}>
              The Relend USDC vault serves as a Global Price Stability Module, maintaining the peg of all rUSDC
              instances. Users supply USDC, receiving RELEND units and additional relending yield from rUSDC supplied to
              partner chains. Collateral markets are selected by risk experts at Relend, B.Protocol and Risk DAO to
              offer secure and competitive yield.
            </Typography>
          </Grid2>

          <Grid2 className="leaderboard" size={{ md: 5, lg: 3 }}>
            <Box
              sx={{
                ...styles.boxStyle,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Typography sx={{ paddingBottom: '2rem', ...styles.leaderboardTitle }}>Leaderboard</Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  height: '100%',
                  width: '100%'
                }}
              >
                {isLoading
                  ? Array(DASHBOARD_CONSTANTS.MAX_LEADERBOARD_DISPLAY)
                    .fill(0)
                    .map((_, index) => (
                      <Box key={index} sx={styles.leaderboardRow}>
                        <Skeleton width={150} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                        <Skeleton width={80} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                      </Box>
                    ))
                  : Object.entries(rewardData)
                    .slice(0, DASHBOARD_CONSTANTS.MAX_LEADERBOARD_DISPLAY)
                    .map(([key, value], index) => (
                      <LeaderboardRow key={key} reward={{ [key]: value }} index={index} />
                    ))}
              </Box>
              <Button onClick={() => setIsLeaderboardModalOpen(true)}>View all</Button>
            </Box>
          </Grid2>
        </Grid2>
        <LeaderboardModal
          open={isLeaderboardModalOpen}
          onClose={() => setIsLeaderboardModalOpen(false)}
          rewardData={rewardData}
        />
      </Grid2>
    </Box>
  );
}
